export type CoverCropRect = { x: number; y: number; width: number; height: number };

// Aspects this close are treated as equal (1080x1920 vs 9/16 rounding).
const SAME_ASPECT_TOLERANCE = 0.01;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const fraction = (value: number | null | undefined) => (
  typeof value === 'number' && Number.isFinite(value) ? clamp(value, 0, 1) : 0.5
);

/**
 * The window of a captured source frame that matches what the export shows:
 * a cover-fit crop to `targetAspect` (width / height). A wider source shows
 * `1 / zoom` of its height and of the cover width, around the preview's
 * reframe center (`centerX` / `centerY`, 0..1 fractions of the source,
 * `null` = centered), clamped inside the frame — the window the preview and
 * the render draw for that crop keyframe. A taller source keeps its full
 * width and is center-cropped vertically (the landscape export has no
 * vertical reframe).
 */
export function computeCoverCropRect({
  sourceWidth,
  sourceHeight,
  targetAspect,
  centerX,
  centerY = null,
  zoom = 1,
}: {
  sourceWidth: number;
  sourceHeight: number;
  targetAspect: number;
  centerX: number | null;
  centerY?: number | null;
  zoom?: number;
}): CoverCropRect {
  const full = { x: 0, y: 0, width: sourceWidth, height: sourceHeight };
  if (!(sourceWidth > 0) || !(sourceHeight > 0) || !(targetAspect > 0)) return full;

  const sourceAspect = sourceWidth / sourceHeight;
  if (Math.abs(sourceAspect - targetAspect) / targetAspect <= SAME_ASPECT_TOLERANCE) return full;

  if (sourceAspect > targetAspect) {
    const scale = Number.isFinite(zoom) && zoom > 1 ? zoom : 1;
    const width = Math.min(sourceWidth, Math.round((sourceHeight * targetAspect) / scale));
    const height = Math.min(sourceHeight, Math.round(sourceHeight / scale));
    const x = clamp(Math.round(fraction(centerX) * sourceWidth - width / 2), 0, sourceWidth - width);
    const y = clamp(Math.round(fraction(centerY) * sourceHeight - height / 2), 0, sourceHeight - height);
    return { x, y, width, height };
  }

  const height = Math.min(sourceHeight, Math.round(sourceWidth / targetAspect));
  const y = Math.round((sourceHeight - height) / 2);
  return { x: 0, y, width: sourceWidth, height };
}
